'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React from 'react'

// walrus
import { accessibilityLabel } from '@components/kit/_support'
import Page from '@components/kit/page'
import Screen from '@components/kit/screen'
import Box from '@components/kit/box'
import Loading from '@components/kit/loading'
import { PollActivity as PollAnswer, IUserActivity } from '@store/User/types'
import FlatList from '@components/kit/flat-list'

// lib
import { ChatContext } from './chat.web'
import { useUserStore } from '@store/User'
import MedSatisfactionPollAnswer from './med-satisfaction-poll-answer.web'
import AdcHocPollAnswer from './ad-hoc-poll-answer.web'

/* -----------------------------------------------------------------------------
 * PollsHistory
 * -------------------------------------------------------------------------- */

// the endpoint answers POLL_ANSWER with OPPORTUNITY_POLL_ANSWER entries mixed
// in, and those carry an empty event_data that has no id to key a row by
const pollAnswersOnly = (results?: IUserActivity[]) =>
  (results ?? []).filter(
    item => item.event_type === 'POLL_ANSWER' && item.event_data?._id != null
  )

const PollsHistory = () => {
  const { userId } = React.useContext(ChatContext)
  const { getUserActivities } = useUserStore.getState()
  const [results, setResults] = React.useState<IUserActivity[]>([])
  const [page, setPage] = React.useState(0)
  const [hasNextPage, setHasNextPage] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(true)
  const [isFetchingNextPage, setIsFetchingNextPage] = React.useState(false)

  React.useEffect(() => {
    setIsLoading(true)
    getUserActivities({ userId, eventType: 'POLL_ANSWER' }).then(response => {
      setResults(pollAnswersOnly(response?.results))
      setPage(response?.page ?? 0)
      setHasNextPage(!!response?.has_next_page)
      setIsLoading(false)
    })
  }, [getUserActivities, userId])

  const loadNextPage = async () => {
    if (!hasNextPage || isFetchingNextPage) {
      return
    }
    setIsFetchingNextPage(true)
    const response = await getUserActivities({
      userId,
      eventType: 'POLL_ANSWER',
      page: page + 1
    })
    setResults(current => [...current, ...pollAnswersOnly(response?.results)])
    setPage(response?.page ?? page + 1)
    setHasNextPage(!!response?.has_next_page)
    setIsFetchingNextPage(false)
  }

  if (isLoading) {
    return (
      <Box mt='64'>
        <Loading />
      </Box>
    )
  }

  const renderItem = ({ item }) =>
    item.event_data.type === 'med_satisfaction' ? (
      <MedSatisfactionPollAnswer poll={item} />
    ) : (
      <AdcHocPollAnswer poll={item} />
    )

  return (
    <Screen {...accessibilityLabel('Polls History')}>
      <Page px='24' py='12'>
        <FlatList
          maxHeight='100%'
          pb='48'
          data={results}
          keyExtractor={(item: PollAnswer) => item.event_data._id.toString()}
          renderItem={renderItem}
          onEndReached={loadNextPage}
          onEndReachedThreshold={0.5}
          ListFooterComponent={
            isFetchingNextPage && (
              <Box alignItems='center' justifyContent='center' py='40'>
                <Loading />
              </Box>
            )
          }
        />
      </Page>
    </Screen>
  )
}

export default PollsHistory
